import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import Link from "@tiptap/extension-link";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import { Markdown } from "@tiptap/markdown";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import javascript from "highlight.js/lib/languages/javascript";
import python from "highlight.js/lib/languages/python";
import typescript from "highlight.js/lib/languages/typescript";
import { createLowlight } from "lowlight";
import type { MouseEvent as ReactMouseEvent } from "react";
import { useMemo } from "react";

import { cn } from "../../utils";
import { FrontmatterPanel } from "../FrontmatterPane";
import { splitSkillMarkdown } from "../skill-frontmatter";

import type { SkillMarkdownPreviewCacheEntry } from "./skill-markdown-preview-cache";
import {
  parseSkillMarkdownToHtml,
  SKILL_MARKDOWN_MARKED_OPTIONS,
} from "./skill-markdown-render";

const lowlight = createLowlight();

lowlight.register("python", python);
lowlight.register("javascript", javascript);
lowlight.register("typescript", typescript);
lowlight.registerAlias({
  python: ["py", "requests", "http"],
  javascript: ["js"],
  typescript: ["ts"],
});

const MARKDOWN_PREVIEW_EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    codeBlock: false,
    link: false,
  }),
  CodeBlockLowlight.configure({
    lowlight,
    languageClassPrefix: "language-",
  }),
  Link.configure({
    openOnClick: false,
    autolink: true,
    linkOnPaste: true,
  }),
  Table.configure({
    resizable: true,
    HTMLAttributes: {
      class: "skill-md-table",
    },
  }),
  TableRow,
  TableHeader,
  TableCell,
  Markdown.configure({
    markedOptions: SKILL_MARKDOWN_MARKED_OPTIONS,
  }),
];

export interface SkillMarkdownPreviewContentProps {
  markdown: string;
  className?: string;
  containerClassName?: string;
  bodyContainerClassName?: string;
  editorContentClassName?: string;
  onBodyClick?: (event: ReactMouseEvent<HTMLDivElement>) => void;
  markdownPreparse?: SkillMarkdownPreviewCacheEntry | null;
}

export const SkillMarkdownPreviewContent = ({
  markdown,
  className,
  containerClassName,
  bodyContainerClassName,
  editorContentClassName,
  onBodyClick,
  markdownPreparse,
}: SkillMarkdownPreviewContentProps) => {
  const splitDocument = useMemo(() => {
    if (markdownPreparse) {
      return markdownPreparse.split;
    }
    return splitSkillMarkdown(markdown);
  }, [markdown, markdownPreparse]);

  const bodyHtmlForEditor = useMemo(() => {
    if (markdownPreparse) {
      return markdownPreparse.bodyHtml;
    }
    return parseSkillMarkdownToHtml(splitDocument.body);
  }, [markdownPreparse, splitDocument.body]);

  const editorProps = useMemo(
    () => ({
      attributes: {
        class: cn(
          "skill-collection-markdown-editor prose prose-sm max-w-none px-4 py-3",
          "prose-headings:font-semibold prose-p:my-2 prose-ul:my-2 prose-ol:my-2",
          className,
        ),
      },
    }),
    [className],
  );

  const editor = useEditor(
    {
      immediatelyRender: false,
      editable: false,
      extensions: MARKDOWN_PREVIEW_EXTENSIONS,
      content: bodyHtmlForEditor,
      editorProps,
    },
    [bodyHtmlForEditor, editorProps],
  );

  if (!editor) {
    return null;
  }

  return (
    <div className={cn("flex h-full min-h-0 flex-col overflow-auto", containerClassName)}>
      <FrontmatterPanel frontmatter={splitDocument.frontmatter} />
      <div
        className={cn("flex min-h-0 flex-1 flex-col overflow-auto", bodyContainerClassName)}
        onClick={onBodyClick}
      >
        <EditorContent editor={editor} className={cn("min-h-full flex-1", editorContentClassName)} />
      </div>
    </div>
  );
};
